import { createSlice } from '@reduxjs/toolkit'
import type { PayloadAction } from '@reduxjs/toolkit'
import { RootState } from '../index'

export interface CategoryItem {
  id: number
  name: string
  slug: string
  categoryId: number
  isSelected: boolean
}

export interface Category {
  categoryName: string
  records: CategoryItem[]
}

interface InterestsState {
  categories: Category[]
  filterText: string
  isLoading: boolean
  error: string | null
  isUpdating: boolean
}

const initialState: InterestsState = {
  categories: [],
  filterText: '',
  isLoading: false,
  error: null,
  isUpdating: false,
}

export const interestsSlice = createSlice({
  name: 'interests',
  initialState,
  reducers: {
    setCategories: (state, action: PayloadAction<Category[]>) => {
      state.categories = action.payload
    },
    setFilterText: (state, action: PayloadAction<string>) => {
      state.filterText = action.payload
    },
    toggleCategoryItem: (
      state,
      action: PayloadAction<{ categoryName: string; itemId: number }>,
    ) => {
      const { categoryName, itemId } = action.payload
      const category = state.categories.find((c) => c.categoryName === categoryName)

      if (category) {
        const item = category.records.find((item) => item.id === itemId)
        if (item) {
          item.isSelected = !item.isSelected
        }
      }
    },
    removeAllSelections: (state) => {
      state.categories.forEach((category) => {
        category.records.forEach((item) => {
          item.isSelected = false
        })
      })
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload
    },
    setUpdating: (state, action: PayloadAction<boolean>) => {
      state.isUpdating = action.payload
    },
  },
})

export const {
  setCategories,
  setFilterText,
  toggleCategoryItem,
  removeAllSelections,
  setLoading,
  setError,
  setUpdating,
} = interestsSlice.actions

export const selectCategories = (state: RootState) => state.interests.categories
export const selectFilterText = (state: RootState) => state.interests.filterText
export const selectFilteredCategories = (state: RootState) => {
  const { categories, filterText } = state.interests

  if (!filterText) return categories

  return categories.filter((category) => {
    if (category.categoryName.toLowerCase().includes(filterText.toLowerCase())) {
      return true
    }

    const hasMatchingItems = category.records.some((item) =>
      item.name.toLowerCase().includes(filterText.toLowerCase()),
    )

    return hasMatchingItems
  })
}
export const selectIsLoading = (state: RootState) => state.interests.isLoading
export const selectError = (state: RootState) => state.interests.error
export const selectIsUpdating = (state: RootState) => state.interests.isUpdating

export default interestsSlice.reducer
